import { FC, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../../utils/currency';

interface EarningsHistoryEntry {
  date: string;
  earnings: number; // in cents
  appointmentsCount: number;
}

interface EarningsChartProps {
  earningsHistory: EarningsHistoryEntry[];
}

interface ChartDataPoint {
  date: string;
  label: string;
  earnings: number;
  earningsCents: number;
  appointments: number;
}

/**
 * Format date for chart label (e.g., "Dec 25")
 */
function formatChartDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartDataPoint }>;
}

/**
 * Custom tooltip component for the earnings chart
 */
function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 p-3">
      <p className="text-sm font-medium text-gray-900 dark:text-slate-100 mb-1">
        {data.label}
      </p>
      <p className="text-lg font-bold text-green-600 dark:text-green-400">
        {formatCurrency(data.earningsCents)}
      </p>
      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
        {data.appointments} {data.appointments === 1 ? 'appointment' : 'appointments'}
      </p>
    </div>
  );
}

export const EarningsChart: FC<EarningsChartProps> = ({ earningsHistory }) => {
  // Transform data for chart
  const chartData = useMemo((): ChartDataPoint[] => {
    // Sort by date (oldest first for left-to-right display)
    const sorted = [...earningsHistory].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return sorted.map((entry) => ({
      date: entry.date,
      label: new Date(entry.date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
      earnings: entry.earnings / 100, // Convert cents to pounds for display
      earningsCents: entry.earnings,
      appointments: entry.appointmentsCount,
    }));
  }, [earningsHistory]);

  if (earningsHistory.length === 0) {
    return null;
  }

  // Calculate max value for y-axis domain
  const maxEarnings = Math.max(...chartData.map((d) => d.earnings));
  const yAxisMax = Math.ceil(maxEarnings / 50) * 50; // Round up to nearest 50

  return (
    <div className="h-64 sm:h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--chart-grid, #e5e7eb)"
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatChartDate}
            tick={{ fontSize: 12, fill: 'var(--chart-text, #6b7280)' }}
            axisLine={{ stroke: 'var(--chart-grid, #e5e7eb)' }}
            tickLine={false}
          />
          <YAxis
            domain={[0, yAxisMax || 100]}
            tickFormatter={(value: number) => `£${value}`}
            tick={{ fontSize: 12, fill: 'var(--chart-text, #6b7280)' }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
          <Bar
            dataKey="earnings"
            fill="#22c55e"
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
